import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { decode } from 'html-entities/lib/index'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import config from 'config'
import PageContainer from 'srcReact/PageContainer/PageContainer'
import EventCalendar from 'srcReact/components/common2/EventCalendar'
import MixrCard from 'srcReact/components/common2/MixrCard'
import Tabs from 'srcReact/components/common2/Tabs'
import NotFound from 'srcReact/components/common2/NotFound'
import { checkNested } from 'srcReact/helpers/validator'
import { isMobileScreen, isTabletScreen, urlSearchParams } from 'srcReact/helpers/utils'
import history from 'srcReact/helpers/history'
import { commonActions } from 'srcReact/components'
import { calendarActions } from './action'
import styles from './styles.module.scss'
import dayjs from 'dayjs'
import { rsvpActions } from 'srcReact/components/common2/RSVP/actions'

export default function CalendarPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    common: { isNative, userLocation, isDesktop },
    calendarEvents: { refreshMyCalendar, selectedTab },
    rsvp: { loading: rsvpLoading, success: rsvpSuccess, error: rsvpError, data: rsvpData }
  } = useSelector((state) => state)
  const [isMobile, setIsMobile] = useState(isMobileScreen())
  const [isTablet, setIsTablet] = useState(isTabletScreen())
  const current_organization = useSelector((state) => state.organization.current_organization)
  const calendarEvents = useSelector((state) => state.calendarEvents)
  const [currentView, setCurrentView] = useState('week')
  const [orgName, setOrgName] = useState('')

  useEffect(() => {
    if (checkNested(current_organization, 'info', 'id')) {
      dispatch(commonActions.addUserPageView(appConstantSettings.CALENDAR_PAGE, current_organization.info.id))
    }
    window.scrollTo(0, 0)
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])

  useEffect(() => {
    if (!rsvpLoading && rsvpSuccess && rsvpData) {
      dispatch(calendarActions.updateRSVPData(rsvpData.mixr_id, rsvpData))
      dispatch(rsvpActions.clearData())
    }
  }, [rsvpLoading, rsvpSuccess])
  useEffect(() => {
    const currentDate = dayjs().format('YYYY-MM-DD')
    const dateFormatted = dayjs(currentDate).toDate()
    let start, end
    if (currentView === 'month') {
      start = dayjs(dateFormatted).startOf('month').format('YYYY-MM-DD')
      end = dayjs(dateFormatted).endOf('month').format('YYYY-MM-DD')
    } else if (currentView === 'week') {
      start = dayjs(dateFormatted).startOf('week').format('YYYY-MM-DD')
      end = dayjs(dateFormatted).endOf('week').format('YYYY-MM-DD')
    } else if (currentView === 'day') {
      start = dayjs(dateFormatted).format('YYYY-MM-DD')
      end = dayjs(dateFormatted).format('YYYY-MM-DD')
    }
    handleData(start, end)
  }, [current_organization?.info?.id, refreshMyCalendar, selectedTab])

  const handleWindowSizeChange = () => {
    setIsMobile(isMobileScreen())
    setIsTablet(isTabletScreen())
  }

  const handleData = (start, end) => {
    // nativeapp
    if (isNative) {
      const payload = {
        id_workplace: urlSearchParams('id_workplace').toString(),
        start_date: start,
        end_date: end,
        latitude: urlSearchParams('lat'),
        longitude: urlSearchParams('lon'),
        with_general_calendar: selectedTab
      }
      dispatch(calendarActions.getCalendarEvents(payload))
      return
    }

    if (checkNested(current_organization, 'info', 'my_access', 'role_as_workplace_member')) {
      const workplaceIds = current_organization.info.my_access.role_as_workplace_member.workplace_list
      if (workplaceIds && workplaceIds.length >= 1 && userLocation) {
        const payload = {
          id_workplace: workplaceIds.toString(),
          start_date: start,
          end_date: end,
          longitude: userLocation.longitude,
          latitude: userLocation.latitude,
          with_general_calendar: selectedTab
        }
        setOrgName(current_organization.info.name)
        dispatch(calendarActions.getCalendarEvents(payload))
      }
    }
  }

  const handleNavigate = (date, view) => {
    let start, end, dateFormatted

    if (view === 'month') {
      dateFormatted = dayjs(date).toDate()
      start = dayjs(dateFormatted).startOf('month').format('YYYY-MM-DD')
      end = dayjs(dateFormatted).endOf('month').format('YYYY-MM-DD')
    } else if (view === 'week') {
      dateFormatted = dayjs(date).toDate()
      start = dayjs(dateFormatted).startOf('week').format('YYYY-MM-DD')
      end = dayjs(dateFormatted).endOf('week').format('YYYY-MM-DD')
    } else if (view === 'day') {
      dateFormatted = dayjs(date).toDate()
      start = dayjs(dateFormatted).format('YYYY-MM-DD')
      end = dayjs(dateFormatted).format('YYYY-MM-DD')
    }
    handleData(start, end)
  }

  const handleClick = () => {
    window.scrollTo(0, 0)
    //nativeapp
    if (isNative) {
      window.location = config.webUrl + '/0/discover'
      return
    }
    history.push(`/0/discover`)
  }

  const renderMyCalendarTitle = (view) => {
    switch (view) {
      case 'day':
        return t('calendar_page.myCalendar.day')
      case 'week':
        return t('calendar_page.myCalendar.week')
      case 'month':
        return t('calendar_page.myCalendar.month')
      default:
        return t('calendar_page.myCalendar.week')
    }
  }
  const renderGeneralCalendarTitle = (view) => {
    switch (view) {
      case 'day':
        return `${t('calendar_page.generalCalendar.day')} ${orgName}`
      case 'week':
        return `${t('calendar_page.generalCalendar.week')} ${orgName}`
      case 'month':
        return `${t('calendar_page.generalCalendar.month')} ${orgName}`
      default:
        return `${t('calendar_page.generalCalendar.week')} ${orgName}`
    }
  }
  const myCalendarEvents = calendarEvents.myCalendar.events

  return (
    <PageContainer
      pageTitle={t('calendar_page.title')}
      pageClassName={`${styles.pageContainer} ${isNative && styles.nativePageContainer}`}
      withHeader={true}
      withSidebar={true}
      withFooter={false}>
      <Tabs type='boxed' className={styles.tabWrap} contentClassName={styles.tabContent} initialActive={selectedTab}>
        <div
          label={t('calendar_page.tabs.myCalendar')}
          onClick={() => dispatch(calendarActions.setSelectedTab(0))}
          className={styles.tabWrap}>
          <EventCalendar handleNavigate={handleNavigate} currentView={currentView} setCurrentView={setCurrentView} />
        </div>
        <div
          label={t('calendar_page.tabs.generalCalendar')}
          onClick={() => dispatch(calendarActions.setSelectedTab(1))}
          className={styles.tabWrap}>
          <EventCalendar handleNavigate={handleNavigate} currentView={currentView} setCurrentView={setCurrentView} />
        </div>
      </Tabs>
      {isDesktop && (
        <div className={styles.mixrListWrap}>
          <h2 className={styles.title}>
            {selectedTab === 1 ? renderGeneralCalendarTitle(currentView) : renderMyCalendarTitle(currentView)}
          </h2>
          {myCalendarEvents.length === 0 ? (
            <div className={styles.notFoundWrap}>
              <NotFound
                title={t('calendar_page.mixr_list.empty')}
                className={styles.notFound}
                handleClick={handleClick}
                button={t('calendar_page.mixr_list.button')}
              />
            </div>
          ) : (
            <div className={styles.mixrList}>
              {myCalendarEvents.map((m, i) => (
                <MixrCard
                  key={i}
                  mixrId={m.mixr_id}
                  groupId={m.group_id}
                  startDate={m.event_utc_start_datetime}
                  endDate={m.event_utc_end_datetime}
                  utcOffset={m.event_utc_offset}
                  timezone={m.timezone}
                  timezoneFullName={m.timezone_name}
                  location={m.display_address}
                  name={decode(m.group_title)}
                  participants={m.number_of_participants}
                  url={m.mixr_url}
                  picture={m.mixr_picture && m.mixr_picture}
                  // notifications={m.notifications}
                  title={decode(m.mixr_title)}
                  description={m.mixr_description}
                  attendance={m.mixr_attending_type}
                  userAttendance={m.user_attending_type}
                  isMixrOver={m.is_over}
                  color={m.is_over ? 'gray' : m.mixr_color ? m.mixr_color : appConstantSettings.DEFAULT_COLOR.name}
                  isVisibility={m.group_visibility !== 'public'}
                  isRecurrence={m.recurrence_type === 'recurring'}
                  isViewModal={true}
                  isInvited={m.is_invited === 1}
                  className={styles.mixrCardWrap}
                  withoutGroupApproval={m.without_group_approval}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </PageContainer>
  )
}
